import { applyResults, byOrder, pendingOf, toWire } from './queueCore.js';

/**
 * Sync engine core (no browser APIs; unit-tested from backend/tests/fieldsync.test.js).
 *
 * deps:
 *   store: { list(): Promise<op[]>, put(op), remove(clientId) }
 *   send(wireOps): Promise<{ results, stopped }> - POST /api/field-sync; throws { status } on HTTP errors, no status
 *                  when the device is offline
 *   isOnline(): boolean
 *   chunkSize: ops per request (the server accepts up to 100)
 *
 * Ops are sent oldest first. A run stops at the first transient failure so a later action never overtakes an earlier
 * one; conflicts and rejections do not stop the run (they wait for the user on the Conflicts list).
 */
export function createSyncEngine({ store, send, isOnline = () => true, chunkSize = 50, now = () => Date.now() }) {
  let running = null;

  async function sendChunk(chunk) {
    try {
      return { res: await send(chunk.map(toWire)) };
    } catch (e) {
      if (!e?.status) return { offline: true, error: e };
      if (e.status === 401) return { authExpired: true, error: e };
      // The whole request was refused (e.g. one op no longer passes validation): send one at a time so one bad op
      // cannot jam the queue.
      if (e.status === 400 && chunk.length > 1) {
        const results = [];
        for (const op of chunk) {
          const one = await sendChunk([op]);
          if (!one.res) return { ...one, partial: { results } };
          results.push(...one.res.results);
          if (one.res.stopped) return { res: { results, stopped: true } };
        }
        return { res: { results, stopped: false } };
      }
      if (e.status === 400) return { res: { results: [{ clientId: chunk[0].clientId, outcome: 'rejected', status: 400, message: e.message }], stopped: false } };
      return { serverError: true, error: e };
    }
  }

  async function runOnce() {
    const summary = { applied: 0, duplicates: 0, conflicts: 0, rejected: 0, results: [], stopped: false, offline: false, authExpired: false, error: null };
    if (!isOnline()) return { ...summary, offline: true, stopped: true };
    const attempted = new Set();
    for (;;) {
      const ops = await store.list();
      // Never resend an op within the same run (a transient 'retry' stays pending for the next run).
      const pending = pendingOf(ops).filter((o) => !attempted.has(o.clientId));
      if (!pending.length) break;
      const chunk = pending.slice(0, chunkSize);
      chunk.forEach((o) => attempted.add(o.clientId));
      const out = await sendChunk(chunk);
      const res = out.res || out.partial;
      if (res) {
        const { remove, update } = applyResults(ops, res, now());
        for (const id of remove) await store.remove(id);
        for (const op of update) await store.put(op);
        for (const r of res.results) {
          summary.results.push(r);
          if (r.outcome === 'applied') summary.applied += 1;
          else if (r.outcome === 'duplicate') summary.duplicates += 1;
          else if (r.outcome === 'conflict') summary.conflicts += 1;
          else if (r.outcome === 'rejected') summary.rejected += 1;
        }
      }
      if (!out.res || out.res.stopped) {
        summary.stopped = true;
        summary.offline = Boolean(out.offline);
        summary.authExpired = Boolean(out.authExpired);
        summary.error = out.error?.message || (out.res?.stopped ? 'Sync paused; it resumes automatically' : null);
        break;
      }
    }
    summary.syncedAt = new Date(now()).toISOString();
    return summary;
  }

  return {
    /** One sync run at a time; a call during a run waits for it and then runs again (new ops may have arrived). */
    async sync() {
      while (running) await running.catch(() => {});
      running = runOnce();
      try {
        return await running;
      } finally {
        running = null;
      }
    },
    get busy() {
      return Boolean(running);
    },
  };
}

export { byOrder };
